import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/sharp-solid-svg-icons";
import React, { FC, useCallback, useMemo, useState } from "react";
import { AccountPersistentFiltersStore } from "store/UserAccounts/UserAccount/AccountPersistentFilters.store";
import { observer } from "mobx-react";
import { TLeadResponderWorkflow } from "types/lead-responder-workflow.type";
import { Box, Button } from "@chakra-ui/react";
import { SingleSelectInput } from "components";
import { TSelectValue } from "types/select-input.type";
import { TPersistentFilter } from "types/persistent-filters.type";
import { PersistentFilterFormPrompt } from "routes/dashboard/components/prompts/PersistentFilterPrompt/PersistentFilterFormPrompt";
import { useActionPrompt } from "utils/react-hooks/useActionPrompt.hook";
import { filterOptionsByLabel } from "utils/select.utils";
import {
  TriggerablePopoverBlock,
  LeadResponderWorkflowEditorBlock,
} from "../common-blocks";

const EDIT_OR_CREATE_PERSISTENT_FILTER_MODAL =
  "edit-or-create-persistent-filter-modal";

const FILTER_FIELD_MAP: Record<string, keyof TLeadResponderWorkflow> = {
  leadFilter: "leadFilterId",
  smsTemplateId: "smsFilterId",
  followUpEmailTemplateId: "emailFilterId",
} as const;

type TFilterFieldName = keyof typeof FILTER_FIELD_MAP;

interface IProps {
  wipWorkflow: Partial<TLeadResponderWorkflow>;
  accountPersistentFiltersStore: AccountPersistentFiltersStore;
  updateWipWorkflow: (workflow: Partial<TLeadResponderWorkflow>) => void;
  isDisabled?: boolean;
  filterFieldName: TFilterFieldName;
  isRemovable?: boolean;
}

export const LeadResponderWorkflowEditorFilterBlock: FC<IProps> = observer(
  ({
    wipWorkflow,
    accountPersistentFiltersStore,
    updateWipWorkflow,
    isDisabled,
    filterFieldName,
    isRemovable = false,
  }) => {
    const { setModal, unSetModal } = useActionPrompt();
    const workflowPropertyName = FILTER_FIELD_MAP[filterFieldName];
    const currentFilterId =
      (wipWorkflow[workflowPropertyName] as number) || null;

    const [selectedFilterId, setSelectedFilterId] = useState<
      TSelectValue<number>
    >(currentFilterId);

    const getFilterById = useCallback(
      (filterId: number | null) => {
        if (!filterId) return undefined;
        return accountPersistentFiltersStore.persistentFiltersMap[filterId];
      },
      [accountPersistentFiltersStore.persistentFiltersMap]
    );

    const currentFilter = useMemo(() => getFilterById(currentFilterId), [
      currentFilterId,
      getFilterById,
    ]);

    const selectedFilter = useMemo(() => getFilterById(selectedFilterId), [
      selectedFilterId,
      getFilterById,
    ]);

    const updateWorkflow = useCallback(
      (filterId: number | null) => {
        updateWipWorkflow({
          ...wipWorkflow,
          [workflowPropertyName]: filterId,
        });
      },
      [wipWorkflow, workflowPropertyName, updateWipWorkflow]
    );

    const handleApply = useCallback(
      (onClose: () => void) => () => {
        if (!!selectedFilterId && !!workflowPropertyName) {
          updateWorkflow(selectedFilterId);
        }
        onClose();
      },
      [selectedFilterId, workflowPropertyName, updateWorkflow]
    );

    const handleRemove = useCallback(
      (onClose: () => void) => () => {
        setSelectedFilterId(null);
        updateWorkflow(null);
        onClose();
      },
      [updateWorkflow]
    );

    const handleOpenModal = useCallback(
      (persistentFilter?: TPersistentFilter) => () => {
        setModal(
          <PersistentFilterFormPrompt
            closePrompt={() =>
              unSetModal(EDIT_OR_CREATE_PERSISTENT_FILTER_MODAL)
            }
            persistentFilter={persistentFilter}
            persistentFiltersStore={accountPersistentFiltersStore}
          />,
          {
            key: EDIT_OR_CREATE_PERSISTENT_FILTER_MODAL,
          }
        );
      },
      [accountPersistentFiltersStore, setModal, unSetModal]
    );

    return (
      <TriggerablePopoverBlock
        onOpen={() => {
          setSelectedFilterId(currentFilterId);
        }}
        triggerElement={
          <LeadResponderWorkflowEditorBlock
            placeholder={"Select filter"}
            iconColor={"responderGrey"}
            icon={<FontAwesomeIcon icon={faFilter} />}
            isDisabled={isDisabled}
            isEmpty={!currentFilter}
          >
            {currentFilter ? (
              currentFilter.name
            ) : (
              <Box textAlign={"center"}>
                <Box fontWeight={"bold"} fontSize={"sm"}>
                  {isRemovable ? "Skip filter" : "Select filter"}
                </Box>
                <Box fontSize={"xs"} color={"gray.500"}>
                  Click to add a filter
                </Box>
              </Box>
            )}
          </LeadResponderWorkflowEditorBlock>
        }
        headerElement={"Select filter"}
        bodyElement={
          <>
            <SingleSelectInput<number>
              value={selectedFilterId}
              options={accountPersistentFiltersStore.persistentFiltersAsOptions}
              onChange={setSelectedFilterId}
              filterFn={filterOptionsByLabel}
              clearable={false}
              placeholder={"Select filter"}
            />
            <Box display={"flex"} justifyContent={"space-between"} pt={2}>
              <Button variant={"link"} onClick={handleOpenModal()}>
                + Create filter
              </Button>
              {!!selectedFilter && (
                <Button
                  variant={"link"}
                  onClick={handleOpenModal(selectedFilter)}
                >
                  + Edit filter
                </Button>
              )}
            </Box>
          </>
        }
        handleApply={handleApply}
        handleRemove={handleRemove}
        isRemovable={isRemovable}
      />
    );
  }
);
